import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface BiaRow {
  readonly biaId: string;
  readonly biaCode: string;
  readonly businessProcess: string;
  readonly criticality: string;
  readonly impactCategory: string;
  readonly status: string;
  readonly maxTolerableDowntimeMinutes: number | null;
}

const CRITICALITY = ['', 'TIER_1', 'TIER_2', 'TIER_3'];
const CATEGORIES = ['', 'FINANCIAL', 'OPERATIONAL', 'COMPLIANCE', 'REPUTATION'];
const STATUSES = ['', 'DRAFT', 'ASSESSED', 'APPROVED'];
const CREATE_CRITICALITY = CRITICALITY.filter(Boolean);
const CREATE_CATEGORIES = CATEGORIES.filter(Boolean);

/**
 * M31 — Business Impact Analysis (§7 / §31). Register of assessed business
 * processes with their criticality tier, impact category, MTD and lifecycle
 * status (BR-02: a BIA must be assessed before it can be approved). Supports
 * creating a BIA, assessing it and approving it — each wired to the guarded
 * /v1/bcm/bia write API. Filterable by tier, impact category and status.
 */
export function BusinessImpactAnalysisPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<BiaRow[]>([]);
  const [criticality, setCriticality] = useState('');
  const [impactCategory, setImpactCategory] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newProcess, setNewProcess] = useState('');
  const [newCriticality, setNewCriticality] = useState('TIER_1');
  const [newCategory, setNewCategory] = useState('OPERATIONAL');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (criticality) params.set('criticality', criticality);
    if (impactCategory) params.set('impactCategory', impactCategory);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: BiaRow[] }>(`/v1/bcm/bia${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, criticality, impactCategory, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m31.bia.err403')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newProcess.trim()) { setActionError(t('m31.bia.errProcess')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/bcm/bia`, {
        method: 'POST',
        body: { businessProcess: newProcess.trim(), criticality: newCriticality, impactCategory: newCategory },
        token,
      });
      setNotice(t('m31.bia.noticeCreated', { process: newProcess.trim() }));
      setNewProcess('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: BiaRow, path: string, label: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/bcm/bia/${r.biaId}/${path}`, { method: 'POST', body: {}, token });
      setNotice(t('m31.bia.actNotice', { code: r.biaCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="business-impact-analysis">
      <h1>{t('m31.bia.title')}</h1>

      <section aria-label="create-bia">
        <h2>{t('m31.bia.createHeading')}</h2>
        <form aria-label="bia-form" onSubmit={(e) => void create(e)}>
          <label>{t('m31.bia.fieldProcess')}<input aria-label="field-business-process" value={newProcess} onChange={(e) => setNewProcess(e.target.value)} /></label>
          <label>{t('m31.bia.fieldCriticality')}
            <select aria-label="field-criticality" value={newCriticality} onChange={(e) => setNewCriticality(e.target.value)}>
              {CREATE_CRITICALITY.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m31.bia.fieldImpact')}
            <select aria-label="field-impact-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CREATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-bia" disabled={busy}>{busy ? t('m31.bia.busyCreate') : t('m31.bia.submitCreate')}</button>
        </form>
        {actionError && <p role="alert" aria-label="bia-error">{actionError}</p>}
        {notice && <p aria-label="bia-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m31.bia.filterCriticality')}
          <select aria-label="filter-criticality" value={criticality} onChange={(e) => setCriticality(e.target.value)}>
            {CRITICALITY.map((c) => <option key={c || 'all'} value={c}>{c || t('m31.bia.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m31.bia.filterImpact')}
          <select aria-label="filter-impact-category" value={impactCategory} onChange={(e) => setImpactCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || t('m31.bia.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m31.bia.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m31.bia.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m31.bia.loading')}</p> : (
        <>
          <p aria-label="bia-count">{t('m31.bia.count', { count: rows.length })}</p>
          <table aria-label="bia-table">
            <thead><tr><th>{t('m31.bia.thCode')}</th><th>{t('m31.bia.thProcess')}</th><th>{t('m31.bia.thCriticality')}</th><th>{t('m31.bia.thImpact')}</th><th>{t('m31.bia.thStatus')}</th><th>{t('m31.bia.thMtd')}</th><th>{t('m31.bia.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.biaId}>
                  <td>{r.biaCode}</td><td>{r.businessProcess}</td><td>{r.criticality}</td>
                  <td>{r.impactCategory}</td><td>{r.status}</td>
                  <td>{r.maxTolerableDowntimeMinutes === null ? '—' : `${r.maxTolerableDowntimeMinutes}m`}</td>
                  <td>
                    {r.status === 'DRAFT' && (
                      <button type="button" aria-label={`assess-${r.biaCode}`} onClick={() => void act(r, 'assess', t('m31.bia.assessed'))}>{t('m31.bia.assess')}</button>
                    )}
                    {r.status === 'ASSESSED' && (
                      <button type="button" aria-label={`approve-${r.biaCode}`} onClick={() => void act(r, 'approve', t('m31.bia.approved'))}>{t('m31.bia.approve')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
